import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { FC } from 'react';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import { actionButtonInput, backgroundColorInput } from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { ActionVariant, BaseAction } from './Action';
import { ComponentHeader } from './ComponentHeader';
import sharedStyle from './shared.module.scss';

interface BuilderInput extends SharedBuilderComponent {
  headline?: string;
  description?: string;
  action: BaseAction;
  actionVariant?: ActionVariant;
}

type Props = BuilderInput & {
  locale: string;
};

const COMPONENT_NAME_DEV = 'DevComponent';

export const DevComponent: FC<Props> = ({
  headline,
  description,
  action,
  actionVariant = ActionVariant.NONE,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;

  return (
    <section
      data-testid="dev-component"
      {...attributes}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <div className={cn('u-wrapper')}>
        <ComponentHeader
          title={headline}
          description={description}
          action={action}
          actionVariant={actionVariant}
          locale={blockLocale}
          headerClassName={'u-wrapper'}
          mobileActionClassName={'u-wrapper'}
        >
          <div />
        </ComponentHeader>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export function componentRegister() {
  Builder.registerComponent(DevComponent, {
    name: COMPONENT_NAME_DEV,
    inputs: [
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'longText',
        defaultValue:
          'Sample Single <span class="u-typography-h1-serif tw:text-brand-primary">Content</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditorDev',
        defaultValue:
          'Long description telling visitor what do we want to highlight in this month',
      },
      backgroundColorInput(COLOR_NAME_WHITE),
      ...actionButtonInput({}),
    ],
    noWrap: true,
  });
  return COMPONENT_NAME_DEV;
}
